/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * myPayments
 * --------------
 * The signed-in person's own payments ("My payments", /feed/payments).
 *
 * ONE SCOPED READ carries every fact the page needs:
 * GET /arcee/payments/mine/ is filtered to the caller's own
 * ApplicationUser server-side, from the session. The browser does not say
 * whose payments to fetch and cannot ask for anybody else's.
 *
 * NAMES ARE A SECOND, OPTIONAL LAYER. The endpoint is generic by design — the
 * payments layer must not know that a line item's parent is an event — so it
 * returns ids and this module turns them into words using the public event
 * read the pass cards already use. That read may fail, and the page is built
 * so that it does not matter: see `rowTitle`.
 *
 * Everything here is plain functions; the page renders what they return.
 */

import APIHelper from '../../api/APIHelper';
import envConfig from '../../config/envConfig';
import { fetchPublicRecordDetail } from '../../hooks/usePublicTableView';
import { buildRelatedScope } from '../../utils/relatedScope';
import { parseStoredUtc } from '../../utils/zonedDateTime';

const api = new APIHelper({
  apiUrl: envConfig.backendApiUrl,
  tokenUrl: envConfig.backendTokenUrl,
  clientId: envConfig.clientKey,
  clientSecret: envConfig.clientSecret,
});

export const PAYMENTS_ENDPOINT = '/arcee/payments/history/';

/*
 * WHERE THE EVENT'S NAME LIVES, told to the server rather than known by it.
 *
 * The payments endpoint holds a line item's parent as an id and nothing more —
 * it must not learn that this application's parent is an event. So the request
 * names the table and column, the server validates both against this
 * application's own metadata (and refuses anything not marked public), and the
 * titles come back WITH the payments instead of costing a request each.
 *
 * These two constants are the only place the portal's table naming leaks into
 * the payments screen.
 */
export const EVENT_TABLE = 'event';
export const EVENT_LABEL_COLUMN = 'title';
export const EVENT_SLUG = 'event';
export const PAGE_SIZE = 25;

/**
 * The chips, and which stored statuses each one stands for.
 *
 * Internal statuses are not customer-facing English, and some of them say the
 * same thing to a buyer: `in_progress` and `pending` are both "we are waiting",
 * and the difference between them is ours, not theirs. The chip sends the
 * stored values so the SERVER filters and `count` stays truthful — filtering in
 * the browser would page wrongly.
 */
export const STATUS_FILTERS = [
  { key: 'all', label: 'All', statuses: null },
  { key: 'successful', label: 'Successful', statuses: ['paid'] },
  { key: 'processing', label: 'Processing', statuses: ['in_progress', 'pending'] },
  { key: 'incomplete', label: 'Not completed', statuses: ['cancelled', 'other'] },
  { key: 'refunded', label: 'Refunded', statuses: ['returned'] },
];

/**
 * A stored status → what the buyer is told, and the tone to say it in.
 *
 * `note` is the page's OWN words for what happened. The server's
 * `failure_reason` is deliberately not sent (it is `str(exc)` from a gateway
 * call and can carry driver internals), so this is where a failed payment gets
 * explained.
 */
const STATUS_COPY = {
  paid: { label: 'Successful', tone: 'ok', note: '' },
  pending: {
    label: 'Processing', tone: 'wait',
    note: 'Your bank is still confirming this payment. It usually settles within a few minutes.',
  },
  in_progress: {
    label: 'Awaiting payment', tone: 'wait',
    note: 'This payment was started but never completed. Nothing has been charged to you.',
  },
  cancelled: {
    label: 'Not completed', tone: 'no',
    note: 'This payment did not go through, so nothing was charged. If your bank shows an amount held against it, it is released automatically.',
  },
  returned: {
    label: 'Refunded', tone: 'ref',
    note: 'This payment has been refunded. Banks usually credit a refund back within 5–7 working days.',
  },
  other: {
    label: 'Needs attention', tone: 'no',
    note: 'Something went wrong with this payment. If an amount was deducted, write to us with the order ID and we will sort it out.',
  },
};

export function statusCopy(status) {
  return STATUS_COPY[status] || { label: 'Unknown', tone: 'wait', note: '' };
}

const text = (v) => String(v ?? '').trim();

/** A relation value → its id. It may arrive bare, as {id}, or as {data:{id}}. */
function idFrom(value) {
  if (value && typeof value === 'object') return value.id ?? value.data?.id ?? '';
  return value;
}
const isPositive = (v) => Number(v) > 0;

/** "24 Oct 2026, 7:12 pm" in the reader's own zone, or '' when unparseable. */
export function stampLabel(value) {
  const at = value ? parseStoredUtc(value) : null;
  if (!at) return '';
  return `${at.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}, `
    + at.toLocaleTimeString('en-GB', { hour: 'numeric', minute: '2-digit', hour12: true });
}

/** "₹1,499.00" — INR gets its symbol, anything else keeps its code. */
export function money(amount, currency) {
  const n = Number(amount);
  const value = Number.isFinite(n)
    ? n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : text(amount);
  return text(currency) === 'INR' ? `₹${value}` : `${text(currency)} ${value}`;
}

/**
 * One API payment → what a row draws.
 *
 * `eventId` and `registrationIds` are the handles the name layer works from.
 * Nothing here needs those names to produce a complete, correct row.
 */
export function toPaymentRow(record) {
  const items = Array.isArray(record?.items) ? record.items : [];
  const copy = statusCopy(record?.status);
  const refunded = record?.refunded_amount;

  return {
    orderId: text(record?.orderId ?? record?.order_id),
    status: text(record?.status),
    statusLabel: copy.label,
    tone: copy.tone,
    note: copy.note,

    createdAt: record?.created_at || null,
    stamp: stampLabel(record?.created_at),
    sortKey: parseStoredUtc(record?.created_at || '')?.getTime() || 0,

    currency: text(record?.currency),
    total: money(record?.amount, record?.currency),
    gross: money(record?.gross_amount, record?.currency),
    discount: isPositive(record?.discount_amount)
      ? money(record.discount_amount, record?.currency) : '',
    tax: isPositive(record?.tax_amount) ? money(record.tax_amount, record?.currency) : '',
    refunded: isPositive(refunded) ? money(refunded, record?.currency) : '',
    refundStatus: text(record?.refund_status),

    // The id the browser will try to turn into an event title. The first line
    // decides it: a cart is one event's seats, so every line shares a parent.
    eventId: text(items[0]?.parent_id),
    // The event's name, ALREADY RESOLVED by the server from the label columns
    // sent above. Empty when it could not be — the row then shows its generic
    // label, exactly as it always did.
    eventTitle: text(items[0]?.parent_label),
    // Which created rows this payment produced, for the activity lookup. A
    // line that never became a row contributes nothing and is skipped.
    registrationIds: items.map((item) => text(item?.instance_id)).filter(Boolean),
    // The generic label, which is what a row shows when no event name can be
    // found. "Event registration", never "Event #42".
    fallbackTitle: text(items[0]?.name) || 'Payment',
    seats: items.length,
  };
}

/**
 * What a row's title line should read, given whatever the name layer has
 * managed to resolve so far.
 *
 * FOUR OUTCOMES, KEPT APART ON PURPOSE:
 *   not asked yet      → `pending: true`, and the row shimmers its title only
 *   resolved           → "Hyderabad Marathon 2026 — Half Marathon"
 *   event only         → "Hyderabad Marathon 2026"
 *   event unresolved   → "Event registration" (the generic label)
 *
 * The amount, status, date and order id are already drawn in every one of
 * them, so a page on which nothing resolved is still fit for its purpose: a
 * person can see what they were charged and quote an order id to support.
 */
export function rowTitle(row, naming, regActivities) {
  // THE TITLE NO LONGER WAITS FOR ANYTHING. It arrives with the payment
  // itself, so there is no third "still asking" state for it any more — only
  // the activity, which is a suffix on a line that already reads correctly.
  const title = row.eventTitle || row.fallbackTitle;
  if (!row.eventTitle || !row.eventId) return { title, activity: '', pending: false };
  const entry = naming?.[row.eventId];
  if (!entry) return { title, activity: '', pending: false };
  return { title, activity: activityFor(row, entry, regActivities), pending: false };
}

/**
 * One page of the signed-in person's payments.
 *
 * Throws an Error carrying `kind` ('auth' for 401) so the page can tell "sign
 * in again" from "that failed" — the same convention fetchMyCertificates uses.
 */
export async function fetchMyPayments({ page = 1, pageSize = PAGE_SIZE, statuses = null } = {}) {
  const params = new URLSearchParams({
    application_id: String(envConfig.applicationId ?? ''),
    page: String(page),
    page_size: String(pageSize),
  });
  if (statuses && statuses.length) params.set('status', statuses.join(','));
  // Ask for the event titles in the same breath. A server that cannot honour
  // it simply omits them and the rows fall back to their generic label.
  params.set('label_table', EVENT_TABLE);
  params.set('label_column', EVENT_LABEL_COLUMN);

  let response;
  try {
    response = await api.apiCall(`${PAYMENTS_ENDPOINT}?${params.toString()}`, {
      method: 'GET',
      credentials: 'include',
    });
  } catch (cause) {
    const error = new Error('The payments could not be read.');
    error.kind = 'network';
    throw error;
  }

  if (!response.ok) {
    const error = new Error(`The payments could not be read (${response.status}).`);
    error.kind = response.status === 401 || response.status === 403 ? 'auth' : 'server';
    throw error;
  }

  const body = await response.json().catch(() => null);
  const results = Array.isArray(body?.results) ? body.results : [];

  return {
    count: Number(body?.count) || 0,
    hasNext: Boolean(body?.next),
    hasPrevious: Boolean(body?.previous),
    rows: results.map(toPaymentRow),
  };
}

/**
 * Every event on the page in ONE request, or `null` if that is not available.
 *
 * WHY THIS EXISTS: naming used to cost one public read PER DISTINCT EVENT, so
 * a page of ten payments across ten events cost ten requests. This asks the
 * batch read endpoint for all of them at once — `event` for the titles and
 * `event_activity` for the names — taking the page to three requests however
 * many events it holds.
 *
 * IT IS AN OPTIMISATION, NOT A REQUIREMENT. The batch endpoint authorises every
 * operation individually and REJECTS THE WHOLE BATCH if one is refused, and
 * whether a participant may read `event` this way is a per-deployment grant we
 * cannot assume. So a refusal returns `null` and the caller quietly falls back
 * to the per-event public reads, which is exactly what it did before. Nothing
 * the reader sees changes either way.
 *
 * DELIBERATELY ITS OWN REQUEST, never added to the registration-activity read
 * beside it: one rejected operation would take that read down with it and cost
 * every row its activity name.
 *
 * It asks for ONE table. It used to ask for `event` as well, for the titles —
 * those now arrive with the payments themselves, so the only thing left to
 * fetch is the activity names. Fewer operations, fewer ways to be refused, and
 * a refusal here can no longer cost a row its title.
 *
 * `activityMode` is not available on this path (it is a column of `event`),
 * so the "All legs" rule for a SEQUENTIAL event only applies on the per-event
 * fallback below. A sequential event resolved through the batch shows the
 * activity actually booked, or nothing — never a wrong name.
 */
export async function fetchEventNamingBatch(eventIds) {
  const ids = Array.from(new Set((eventIds || []).map(text).filter(Boolean)));
  if (!ids.length) return {};

  let response;
  try {
    response = await api.apiCall('/arcee/application/table/read/records/', {
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify({
        organization_id: envConfig.organizationId,
        application_id: envConfig.applicationId,
        operations: [
          { ref: 'acts', table: 'event_activity', fields: ['id', 'name', 'event_id'],
            where: { event_id: ids }, limit: 500 },
        ],
      }),
    });
  } catch (_) {
    return null;
  }
  if (!response.ok) return null;

  const body = await response.json().catch(() => null);
  const acts = body?.results?.acts;
  if (!acts || acts.status === 'error' || !Array.isArray(acts.data)) return null;

  // Every id that was ASKED for gets an entry, even an empty one. An event
  // with no activity row is a settled answer, not an unanswered question, and
  // recording it stops the caller asking again on every render.
  const out = {};
  ids.forEach((id) => { out[id] = { title: '', activityMode: '', activities: [] }; });
  acts.data.forEach((row) => {
    const owner = text(idFrom(row?.event_id));
    if (out[owner]) out[owner].activities.push(row);
  });
  return out;
}

/**
 * An event's title and its activities, or `null` when it could not be read.
 *
 * `null` IS A SETTLED ANSWER, not an error to surface. A withdrawn, drafted or
 * deleted event legitimately vanishes from the public read, and a transaction
 * for it must keep working for ever — it simply shows its generic label. The
 * caller never retries a `null`.
 */
export async function fetchEventNaming(eventId) {
  try {
    const data = await fetchPublicRecordDetail(EVENT_SLUG, eventId);
    const record = data?.record || null;
    if (!record) return null;
    const scope = buildRelatedScope(data?.related);
    return {
      title: text(record.title),
      activityMode: text(record.activity_mode).toUpperCase(),
      activities: Array.isArray(scope.categories) ? scope.categories : [],
    };
  } catch (_) {
    return null;
  }
}

/**
 * Which activity to name for a transaction, from the event's own activities
 * and the registration → activity map.
 *
 * Same precedence as the pass cards (activityNameFor in PassCheckInBridge):
 * the activity actually booked wins, a sequential event with no activity of
 * its own covers every leg, and a single-activity event can only be that one.
 * Returns '' when none of those applies — a row then shows the event alone,
 * which is a complete row, not a broken one.
 */
export function activityFor(row, naming, regActivities) {
  if (!naming) return '';
  const list = Array.isArray(naming.activities) ? naming.activities : [];
  const booked = (row.registrationIds || [])
    .map((id) => regActivities?.[String(id)])
    .find((activityId) => activityId !== undefined && activityId !== null);
  const named = booked !== undefined && booked !== null
    ? list.find((activity) => String(activity?.id) === String(booked))
    : null;
  if (named && typeof named.name === 'string') return named.name.trim();
  if (naming.activityMode === 'SEQUENTIAL') return 'All legs';
  if (list.length === 1 && typeof list[0]?.name === 'string') return list[0].name.trim();
  return '';
}
